import { getBlobticUrl } from '../config/api.config';
import { globalErrorHandler } from '../utils/GlobalErrorHandler';

// Types pour le système de zoom
export interface ZoomAction {
  OKey: string;
  [key: string]: string; // Paramètres dynamiques (USER_MAIL, USER_PASS, etc.)
}

export interface ZoomGrid {
  champs: string; // Champs séparés par des virgules
  labels: string; // Labels séparés par des virgules
}

export interface ZoomObject {
  action: ZoomAction;
  grill: ZoomGrid;
}

export interface ZoomData {
  [key: string]: any; // Données dynamiques retournées par le serveur
}

export interface ZoomResult {
  success: boolean;
  data: ZoomData[];
  message?: string;
  error?: string;
}

export interface ZoomConfig {
  title: string;
  width?: number;
  height?: number;
  searchable?: boolean;
  selectable?: boolean;
  multiSelect?: boolean;
  hiddenColumns?: string[];
}

/**
 * Service générique pour gérer tous les zooms de l'application BLOBTIC
 * Responsable de l'affichage des popups avec grilles et de la communication avec le serveur
 */
export class BlobticZoomService {
  private static instance: BlobticZoomService;
  private zoomObjects: Map<string, ZoomObject> = new Map();
  private zoomConfigs: Map<string, ZoomConfig> = new Map();

  private constructor() {
    this.initializeDefaultZooms();
  }

  public static getInstance(): BlobticZoomService {
    if (!BlobticZoomService.instance) {
      BlobticZoomService.instance = new BlobticZoomService();
    }
    return BlobticZoomService.instance;
  }

  /**
   * Initialise les zooms par défaut de l'application
   */
  private initializeDefaultZooms(): void {
    // Zoom des agences (dossiers)
    this.registerZoom('dossier', {
      action: {
        OKey: "Blobtic_Sys_User,sys_user,userdossier"
      },
      grill: {
        champs: 'DOS_CODE,DOS_LIB',
        labels: 'Code agence,Libellé agence'
      }
    }, {
      title: 'zoom.title.agency',
      width: 800,
      height: 600,
      searchable: true,
      selectable: true,
      multiSelect: false
    });

    // Zoom des agences 
    this.registerZoom('agency', {
      action: {
        OKey: "Blobtic_Dossier,dossier,select"
      },
      grill: {
        champs: 'DOS_UIDF,DOS_IDF,DOS_CODE,DOS_LIB,DOS_EMAIL,DOS_TELPH,DOS_ADR1,DOS_START_DATE,DOS_END_DATE,DOS_CLOSEDPLNG,LAST_UPDT,DOS_NIF,DOS_NIS,DOS_RC,DOS_AI,DOS_LOGO',
        labels: 'UID,ID,Code,Nom,Email,Téléphone,Adresse,Date Début,Date Fin,Date Clôture,Dernière MAJ,NIF,NIS,RC,AI,Logo'
      }
    }, {
      title: 'zoom.title.agency',
      width: 900,
      height: 600,
      searchable: true,
      selectable: true,
      multiSelect: false,
      hiddenColumns: ['DOS_UIDF', 'DOS_IDF', 'LAST_UPDT', 'DOS_NIF', 'DOS_NIS', 'DOS_RC', 'DOS_AI', 'DOS_LOGO']
    });
    // Zoom des utilisateurs (legacy key)
    this.registerZoom('utilisateur', {
      action: {
        OKey: "Blobtic_Sys_User,sys_user,list_users"
      },
      grill: {
        champs: 'USER_ID,USER_MAIL,USER_NAME,USER_PROFILE',
        labels: 'ID,Email,Nom,Profil'
      }
    }, {
      title: 'zoom.title.user',
      width: 900,
      height: 600,
      searchable: true,
      selectable: true,
      multiSelect: false
    });

    // Zoom des utilisateurs (standard key)
    this.registerZoom('user', {
      action: {
        OKey: "Blobtic_Sys_User,sys_user,select"
      },
      grill: {
        champs: 'USR_IDF,USR_CODE,USR_LIB,PRF_LIB,USR_MAIL,USR_TELPOR,USR_START_DATE,USR_END_DATE,LAST_UPDT',
        labels: 'ID,Code,Nom,Profil,Email,Téléphone,Date Début,Date Fin,Date MAJ'
      }
    }, {
      title: 'zoom.title.user',
      width: 900,
      height: 600,
      searchable: true,
      selectable: true,
      multiSelect: false,
      hiddenColumns: ['USR_IDF', 'LAST_UPDT']
    });

    // Zoom des profils
    this.registerZoom('profil', {
      action: {
        OKey: "Blobtic_Sys_Profil,Sys_Profil,Select"
      },
      grill: {
        champs: 'PRF_IDF,PRF_CODE,PRF_LIB,PRF_NIVEAU,LAST_UPDT,USER_IDF,USR_LIB,PRF_ADMIN',
        labels: 'ID,Code,Libellé,Niveau,Date MAJ,ID User,Utilisateur,Admin'
      }
    }, {
      title: 'zoom.title.profile',
      width: 700,
      height: 500,
      searchable: true,
      selectable: true,
      multiSelect: false,
      hiddenColumns: ['PRF_IDF', 'PRF_NIVEAU', 'LAST_UPDT', 'USER_IDF', 'USR_LIB']
    });

    // Zoom des salariés
    this.registerZoom('salarie', {
      action: {
        OKey: "Blobtic_Sys_User,sys_user,list_employees"
      },
      grill: {
        champs: 'EMP_ID,EMP_CODE,EMP_NAME,EMP_FIRSTNAME,EMP_EMAIL',
        labels: 'ID,Code,Nom,Prénom,Email'
      }
    }, {
      title: 'zoom.title.employee',
      width: 900,
      height: 600,
      searchable: true,
      selectable: true,
      multiSelect: false
    });

    // Zoom des utilisateurs (Nouvelle version)
    this.registerZoom('user', {
      action: {
        OKey: "Blobtic_Sys_User,sys_user,select"
      },
      grill: {
        champs: 'USR_UIDF,USR_IDF,USR_CODE,USR_LIB,USR_PASS,PRF_IDF,USR_START_DATE,USR_END_DATE,LAST_UPDT,USER_IDF,USR_TYPE,DOS_IDF_DEFAUT,USR_MAIL,USR_PASS_DATE,USR_PASS_DAYS,USR_NUMERO,JOB_IDF,USR_DATA,USERDATA_IDF,USR_MMO,USR_TELPOR,PRF_CODE,PRF_LIB,USERDATA_CODE,USERDATA_LIB,USER_LIB',
        labels: 'UID,ID,Code,Libellé,Mot de passe,Profil ID,Date Début,Date Fin,Dernière MAJ,User ID,Type,Dossier ID,Email,Date Exp Pass,Jours Alerte,Numéro,Job ID,User Data,License ID,Mémo,Mobile,Code Profil,Libellé Profil,Code Licence,Libellé Licence,MAJ Par'
      }
    }, {
      title: 'zoom.title.user',
      width: 900,
      height: 600,
      searchable: true,
      selectable: true,
      multiSelect: false,
      hiddenColumns: [
        'USR_UIDF', 'USR_IDF', 'PRF_IDF', 'USER_IDF', 'DOS_IDF_DEFAUT', 'JOB_IDF', 'USR_DATA', 'USERDATA_IDF', 'USR_TELPOR',
        'USR_START_DATE', 'USR_END_DATE', 'LAST_UPDT', 'USR_TYPE', 'USR_MAIL', 'USR_PASS', 'USR_PASS_DATE', 'USR_PASS_DAYS', 'USR_NUMERO',
        'USR_MMO', 'PRF_CODE', 'USERDATA_CODE', 'USERDATA_LIB', 'USER_LIB'
      ]
    });

    // Zoom des licences
    this.registerZoom('license', {
      action: {
        OKey: "Blobtic_Sys_User,sys_user_lic,select"
      },
      grill: {
        champs: 'USERDATA_UIDF,USERDATA_IDF,USERDATA_CODE,USERDATA_LIB,USERDATA_APP,USERDATA_START_DATE,USERDATA_END_DATE',
        labels: 'UID,ID,Code,Libellé,Application,Date Début,Date Fin'
      }
    }, {
      title: 'zoom.title.license',
      width: 800,
      height: 500,
      searchable: true,
      selectable: true,
      multiSelect: false,
      hiddenColumns: ['USERDATA_UIDF', 'USERDATA_IDF']
    });

    // ---------------------------------------------------------------------------
    // MAKER Zooms
    // ---------------------------------------------------------------------------

    // ---------------------------------------------------------------------------
    // MAKER Zooms
    // ---------------------------------------------------------------------------

    // Project
    this.registerZoom('maker_project', {
      action: { OKey: "Blobtic_Maker,Maker_Project,Select" },
      grill: {
        champs: 'PRJ_IDF,PRJ_CODE,PRJ_LIB,PRJ_DESCRIPTION,CUS_LIB,PRJ_STATUS,PRJ_START_DATE,LAST_UPDT',
        labels: 'ID,Code,Project Name,Description,Customer,Status,Start Date,Last Update'
      }
    }, {
      title: 'Projects',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['PRJ_IDF', 'PRJ_DESCRIPTION']
    });

    // Estimate
    this.registerZoom('maker_estimate', {
      action: { OKey: "Blobtic_Maker,Maker_Estimate,Select" },
      grill: {
        champs: 'EST_IDF,EST_CODE,EST_LIB,PRJ_LIB,EST_AMOUNT,EST_STATUS,LAST_UPDT,USR_LIB',
        labels: 'ID,Code,Estimate,Project,Amount,Status,Last Update,User'
      }
    }, {
      title: 'Estimates',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['EST_IDF']
    });

    // Profile (Master Data for Design)
    this.registerZoom('maker_profile', {
      action: { OKey: "Blobtic_Maker,Maker_Profile,Select" },
      grill: {
        champs: 'PRF_IDF,PRF_CODE,PRF_LIB,PRF_TYPE,PRF_WIDTH,PRF_HEIGHT,PRF_PRICE,PRF_STOCK',
        labels: 'ID,Code,Profile Name,Type,Width,Height,Price,Stock'
      }
    }, {
      title: 'Profiles',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['PRF_IDF']
    });

    // Window (Design Item)
    this.registerZoom('maker_window', {
      action: { OKey: "Blobtic_Maker,Maker_Window,Select" },
      grill: {
        champs: 'WIN_IDF,PRJ_LIB,PRF_LIB,WIN_WIDTH,WIN_HEIGHT,WIN_TYPE',
        labels: 'ID,Project,Profile,Width,Height,Type'
      }
    }, {
      title: 'Windows',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['WIN_IDF']
    });

    // Door (Design Item)
    this.registerZoom('maker_door', {
      action: { OKey: "Blobtic_Maker,Maker_Door,Select" },
      grill: {
        champs: 'DOR_IDF,PRJ_LIB,PRF_LIB,DOR_WIDTH,DOR_HEIGHT,DOR_TYPE',
        labels: 'ID,Project,Profile,Width,Height,Type'
      }
    }, {
      title: 'Doors',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['DOR_IDF']
    });

    // Production (Components)
    this.registerZoom('maker_component', {
      action: { OKey: "Blobtic_Maker,Maker_Component,Select" },
      grill: {
        champs: 'CMP_IDF,CMP_CODE,CMP_LIB,MAT_LIB,CMP_QUANTITY,CMP_UNIT_PRICE,LAST_UPDT',
        labels: 'ID,Code,Component,Material,Qty,Price,Last Update'
      }
    }, {
      title: 'Production Components',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['CMP_IDF']
    });

    // Material
    this.registerZoom('maker_material', {
      action: { OKey: "Blobtic_Maker,Maker_Material,Select" },
      grill: {
        champs: 'MAT_IDF,MAT_CODE,MAT_LIB,MAT_TYPE,MAT_PRICE,MAT_UNIT,MAT_STOCK,LAST_UPDT',
        labels: 'ID,Code,Material,Type,Price,Unit,Stock,Last Update'
      }
    }, {
      title: 'Materials',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['MAT_IDF']
    });

    // Customer
    this.registerZoom('maker_customer', {
      action: { OKey: "Blobtic_Maker,Maker_Customer,Select" },
      grill: {
        champs: 'CUS_IDF,CUS_CODE,CUS_LIB,CUS_COMPANY,CUS_EMAIL,CUS_PHONE,CUS_CITY,LAST_UPDT',
        labels: 'ID,Code,Name,Company,Email,Phone,City,Last Update'
      }
    }, {
      title: 'Customers',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['CUS_IDF']
    });

    // Report
    this.registerZoom('maker_report', {
      action: { OKey: "Blobtic_Maker,Maker_Report,Select" },
      grill: {
        champs: 'REP_IDF,REP_CODE,REP_LIB,REP_DESCRIPTION,LAST_UPDT,USR_LIB',
        labels: 'ID,Code,Label,Description,Last Update,User'
      }
    }, {
      title: 'Reports',
      width: 1000,
      height: 700,
      searchable: true,
      selectable: true,
      hiddenColumns: ['REP_IDF', 'REP_DESCRIPTION']
    });
  }

  /**
   * Enregistre un nouveau zoom
   */
  public registerZoom(zoomKey: string, zoomObject: ZoomObject, config: ZoomConfig): void {
    this.zoomObjects.set(zoomKey, zoomObject);
    this.zoomConfigs.set(zoomKey, config);
  }

  /**
   * Récupère un zoom par sa clé
   */
  public getZoom(zoomKey: string): ZoomObject | undefined {
    return this.zoomObjects.get(zoomKey);
  }

  /**
   * Récupère la configuration d'un zoom
   */
  public getZoomConfig(zoomKey: string): ZoomConfig | undefined {
    return this.zoomConfigs.get(zoomKey);
  }

  /**
   * Lance une action de zoom avec des paramètres dynamiques
   */
  public async executeZoomAction(
    zoomKey: string,
    dynamicParams: Record<string, string> = {},
    signal?: AbortSignal
  ): Promise<ZoomResult> {
    try {
      const zoomObject = this.getZoom(zoomKey);
      if (!zoomObject) {
        return {
          success: false,
          data: [],
          error: `Zoom '${zoomKey}' non trouvé`
        };
      }

      // Fusionner les paramètres de base avec les paramètres dynamiques
      const actionParams = {
        ...zoomObject.action,
        ...dynamicParams
      };

      // Appel au serveur
      const response = await fetch(getBlobticUrl('ZOOM'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Pour userdossier, pas besoin de token d'authentification
          ...(sessionStorage.getItem('blobtic_token') && {
            'Authorization': `Bearer ${sessionStorage.getItem('blobtic_token')}`
          })
        },
        body: JSON.stringify({
          zoomKey,
          action: actionParams
          // grill: zoomObject.grill // REMOVED as per request to return all columns
        }),
        signal
      });



      if (!response.ok) {
        // Essayer de récupérer le message d'erreur spécifique de l'API
        let errorMessage = `Erreur HTTP: ${response.status}`;
        let errorDetails = '';

        try {
          const errorData = await response.json();

          // GESTION ERREUR SESSION PERDUE
          if (errorData.code === 'SESSION_LOST' || response.status === 401 || response.status === 403 || errorData.error === 'SessionExpired') {
            globalErrorHandler.handleSessionLost();
            throw new Error(errorData.error || "Session expirée");
          }

          // Construire un message d'erreur structuré
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          }

          // Ajouter des détails supplémentaires si disponibles
          const details = [];
          if (errorData.code) details.push(`Code: ${errorData.code}`);
          if (errorData.timestamp) details.push(`Heure: ${errorData.timestamp}`);
          if (errorData.path) details.push(`Endpoint: ${errorData.path}`);

          if (details.length > 0) {
            errorDetails = ` (${details.join(', ')})`;
          }

        } catch (e) {
          // Si c'est déjà notre erreur de session, on la propage
          if (e instanceof Error && e.message === "Session expirée") throw e;
          // Sinon si on ne peut pas parser le JSON, on garde le message par défaut
        }

        throw new Error(errorMessage + errorDetails);
      }

      if (response.status === 204) {
        return {
          success: true,
          data: [],
          message: 'Opération effectuée avec succès'
        };
      }

      const result = await response.json().catch(() => ({}));
      if (result && (result.code === 'SESSION_LOST' || result.error === 'SessionExpired' || (result.success === false && result.code === 'SESSION_LOST'))) {
        globalErrorHandler.handleSessionLost();
        return {
          success: false,
          data: [],
          error: result.error || result.message || 'Session expirée'
        };
      }
      return {
        success: true,
        data: result.data || [],
        message: result.message
      };

    } catch (error) {
      console.error('Erreur lors de l\'exécution du zoom: ', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Exécute une action brute sans configuration de zoom préalable
   * Utile pour les appels API backend directs (Notifications, Chat, etc.)
   */
  public async executeRawAction(
    oKey: string,
    params: Record<string, any> = {}
  ): Promise<ZoomResult> {
    try {
      const response = await fetch(getBlobticUrl('ZOOM'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionStorage.getItem('blobtic_token') && {
            'Authorization': `Bearer ${sessionStorage.getItem('blobtic_token')}`
          })
        },
        body: JSON.stringify({
          zoomKey: 'CrudOperation',
          action: {
            OKey: oKey,
            ...params
          }
        })
      });

      if (!response.ok) {
        // Error handling similar to executeZoomAction
        const errorData = await response.json().catch(() => ({}));
        if (errorData.code === 'SESSION_LOST' || response.status === 401 || response.status === 403 || errorData.error === 'SessionExpired') {
          globalErrorHandler.handleSessionLost();
        }
        throw new Error(errorData.error || errorData.message || `Erreur HTTP: ${response.status}`);
      }

      if (response.status === 204) {
        return {
          success: true,
          data: [],
          message: 'Opération effectuée avec succès'
        };
      }

      const result = await response.json().catch(() => ({}));
      if (result && (result.code === 'SESSION_LOST' || result.error === 'SessionExpired' || (result.success === false && result.code === 'SESSION_LOST'))) {
        globalErrorHandler.handleSessionLost();
        return {
          success: false,
          data: [],
          error: result.error || result.message || 'Session expirée'
        };
      }
      return {
        success: true,
        data: result.data || [],
        message: result.message
      };

    } catch (error) {
      console.error('Erreur lors de l\'exécution de l\'action brute:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Erreur inconnue'
      };
    }
  }

  /**
   * Parse les champs et labels d'un zoom
   */
  public parseZoomFields(zoomKey: string): { fields: string[], labels: string[] } {
    const zoomObject = this.getZoom(zoomKey);
    if (!zoomObject) {
      return { fields: [], labels: [] };
    }

    const fields = zoomObject.grill.champs.split(',').map(f => f.trim());
    const labels = zoomObject.grill.labels.split(',').map(l => l.trim());

    return { fields, labels };
  }

  /**
   * Valide un zoom avant exécution
   */
  public validateZoom(zoomKey: string, dynamicParams: Record<string, string> = {}): string[] {
    const errors: string[] = [];
    const zoomObject = this.getZoom(zoomKey);

    if (!zoomObject) {
      errors.push(`Zoom '${zoomKey}' non trouvé`);
      return errors;
    }

    // Vérifier les paramètres requis
    const requiredParams = this.getRequiredParams(zoomObject.action.OKey);

    // Exception pour userdossier : si on est authentifié, on n'a pas besoin de USER_MAIL et USER_PASS
    const isAuthenticated = !!sessionStorage.getItem('blobtic_token');

    for (const param of requiredParams) {
      // Si on est authentifié et que c'est l'action userdossier, on ignore les params de connexion
      if (isAuthenticated &&
        zoomObject.action.OKey === 'Blobtic_Sys_User,sys_user,userdossier' &&
        (param === 'USER_MAIL' || param === 'USER_PASS')) {
        continue;
      }

      if (!dynamicParams[param] && !zoomObject.action[param]) {
        errors.push(`Paramètre requis manquant: ${param}`);
      }
    }

    return errors;
  }

  /**
   * Détermine les paramètres requis selon l'action
   */
  private getRequiredParams(oKey: string): string[] {
    const actionMap: Record<string, string[]> = {
      'Blobtic_Sys_User,sys_user,userdossier': ['USER_MAIL', 'USER_PASS'],
      'Blobtic_Sys_User,sys_user,list_users': [],
      'Blobtic_Sys_User,sys_user,list_profiles': [],
      'Blobtic_Sys_User,sys_user,list_employees': []
    };

    return actionMap[oKey] || [];
  }

  /**
   * Liste tous les zooms disponibles
   */
  public listAvailableZooms(): string[] {
    return Array.from(this.zoomObjects.keys());
  }

  /**
   * Supprime un zoom
   */
  public removeZoom(zoomKey: string): boolean {
    const zoomRemoved = this.zoomObjects.delete(zoomKey);
    const configRemoved = this.zoomConfigs.delete(zoomKey);
    return zoomRemoved || configRemoved;
  }

  /**
   * Met à jour un zoom existant
   */
  public updateZoom(zoomKey: string, zoomObject: ZoomObject, config?: ZoomConfig): boolean {
    if (!this.zoomObjects.has(zoomKey)) {
      return false;
    }

    this.zoomObjects.set(zoomKey, zoomObject);
    if (config) {
      this.zoomConfigs.set(zoomKey, config);
    }

    return true;
  }
}

// Export de l'instance singleton
export const blobticZoomService = BlobticZoomService.getInstance();
